import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import {
  getShift,
  createShift,
  updateShift,
  createShiftTeamEntry,
  updateShiftTeamEntry,
  deleteShiftTeamEntry
} from "../../api/ShiftsApi";
import getPlaces, { Place } from "../../api/PlacesApi";
import { getLanguages, Language } from "../../api/LanguagesApi";
import { getUsersByRole, SelectableUser } from "../../api/UsersApi";
import { USER_ROLE } from "../../types/roles";
import { normalizeShiftStatus, ShiftStatus } from "../../types/shiftStatus";
import { normalizeShiftMode, ShiftMode, shiftModeToTipoParam } from "../../types/shiftMode";
import { getPlaceCode } from "../../api/PlacesApi";
import useTimezone from "../../hooks/useTimezone";

const FALLBACK_TIMEZONE = "UTC";

export type TeamEntryForm = {
  localId: string;
  id?: string;
  interviewerUserId: string;
  assistantUserId: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
};

function buildDateTimeIso(date: string, time: string): string {
  return `${date}T${time}:00`;
}

function toUtcIso(date: string, time: string, timezone: string): string {
  return fromZonedTime(buildDateTimeIso(date, time), timezone).toISOString();
}

function createDefaultTeamEntry(referenceDate: string): TeamEntryForm {
  return {
    localId: `new-${Date.now()}`,
    interviewerUserId: "",
    assistantUserId: "",
    startDate: referenceDate,
    startTime: "20:00",
    endDate: referenceDate,
    endTime: "22:00"
  };
}

function isTeamEntryValid(entry: TeamEntryForm, timezone: string): boolean {
  if (!entry.startDate || !entry.startTime || !entry.endDate || !entry.endTime) {
    return false;
  }

  const start = new Date(toUtcIso(entry.startDate, entry.startTime, timezone)).getTime();
  const end = new Date(toUtcIso(entry.endDate, entry.endTime, timezone)).getTime();

  return Number.isFinite(start) && Number.isFinite(end) && end > start;
}

function getCityName(city: Place): string {
  const rawName = city.name;
  return typeof rawName === "string" ? rawName : "";
}

function getUserLabel(user: SelectableUser): string {
  return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || user.id;
}

function normalizeDateParam(value: string | null): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return null;
  }

  const parsed = new Date(`${trimmed}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return trimmed;
}

type UsePlantaoResult = {
  isEditing: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
  mode: ShiftMode;
  setMode: (v: ShiftMode) => void;
  status: ShiftStatus;
  setStatus: (v: ShiftStatus) => void;
  cityId: string;
  setCityId: (v: string) => void;
  citySearch: string;
  setCitySearch: (v: string) => void;
  filteredCities: Place[];
  cityNeedsSelection: boolean;
  languageId: string;
  setLanguageId: (v: string) => void;
  facilitatorUserId: string;
  setFacilitatorUserId: (v: string) => void;
  facilitatorSearch: string;
  setFacilitatorSearch: (v: string) => void;
  filteredFacilitatorUsers: SelectableUser[];
  cities: Place[];
  languages: Language[];
  facilitatorUsers: SelectableUser[];
  interviewerUsers: SelectableUser[];
  assistantUsers: SelectableUser[];
  teamEntries: TeamEntryForm[];
  addTeamEntry: () => void;
  updateTeamEntry: (localId: string, field: keyof Omit<TeamEntryForm, "localId" | "id">, value: string) => void;
  duplicateTeamEntry: (localId: string) => void;
  removeTeamEntry: (localId: string) => void;
  canSubmit: boolean;
  submitDisabledReason: string | null;
  handleSubmit: (e: React.FormEvent) => void;
};

export default function usePlantao(): UsePlantaoResult {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { timezone: configuredTimezone } = useTimezone();
  const timezone = configuredTimezone || FALLBACK_TIMEZONE;
  const isEditing = Boolean(id);
  const today = formatInTimeZone(new Date(), timezone, "yyyy-MM-dd");
  const preselectedDate = useMemo(
    () => normalizeDateParam(
      searchParams.get("dataInicial") || searchParams.get("startDate") || searchParams.get("data")
    ),
    [searchParams]
  );
  const hasAppliedQueryPrefill = useRef(false);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<ShiftMode>(ShiftMode.Presential);
  const [status, setStatus] = useState<ShiftStatus>(ShiftStatus.PRE_OPEN);
  const [cityId, setCityId] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [languageId, setLanguageId] = useState("");
  const [facilitatorUserId, setFacilitatorUserId] = useState("");
  const [facilitatorSearch, setFacilitatorSearch] = useState("");

  const [teamEntries, setTeamEntries] = useState<TeamEntryForm[]>(() => [createDefaultTeamEntry(today)]);
  const [deletedEntryIds, setDeletedEntryIds] = useState<string[]>([]);

  const [cities, setCities] = useState<Place[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [facilitatorUsers, setFacilitatorUsers] = useState<SelectableUser[]>([]);
  const [interviewerUsers, setInterviewerUsers] = useState<SelectableUser[]>([]);
  const [assistantUsers, setAssistantUsers] = useState<SelectableUser[]>([]);

  useEffect(() => {
    getPlaces().then(setCities).catch(console.error);
    getLanguages().then(setLanguages).catch(console.error);
    Promise.all([
      getUsersByRole(USER_ROLE.facilitador),
      getUsersByRole(USER_ROLE.entrevistador),
      getUsersByRole(USER_ROLE.assistente)
    ]).then(([facilitadores, entrevistadores, assistentes]) => {
      setFacilitatorUsers(facilitadores);
      setInterviewerUsers([...entrevistadores, ...facilitadores]);
      setAssistantUsers([...assistentes, ...entrevistadores, ...facilitadores]);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (isEditing || hasAppliedQueryPrefill.current) {
      return;
    }

    hasAppliedQueryPrefill.current = true;

    const tipoQuery = (searchParams.get("tipo") || searchParams.get("modo") || "").toLowerCase();
    if (tipoQuery) {
      setMode(normalizeShiftMode(tipoQuery));
    }

    const preselectedLocalId = searchParams.get("localId") || searchParams.get("placeId") || "";
    if (preselectedLocalId) {
      setCityId(preselectedLocalId.trim());
    }

    if (preselectedDate) {
      setTeamEntries([createDefaultTeamEntry(preselectedDate)]);
    }
  }, [isEditing, preselectedDate, searchParams]);

  useEffect(() => {
    if (!cityId || citySearch) {
      return;
    }

    const normalizedCityCode = cityId.trim();
    const selectedCity = cities.find((city) => getPlaceCode(city) === normalizedCityCode);
    if (selectedCity) {
      setCitySearch(getCityName(selectedCity));
    }
  }, [cityId, citySearch, cities]);

  useEffect(() => {
    if (!facilitatorUserId || facilitatorSearch) {
      return;
    }

    const user = facilitatorUsers.find((u) => u.id === facilitatorUserId);
    if (user) {
      setFacilitatorSearch(getUserLabel(user));
    }
  }, [facilitatorUserId, facilitatorSearch, facilitatorUsers]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getShift(id)
      .then((plantao) => {
        setMode(normalizeShiftMode(plantao.mode));
        setStatus(normalizeShiftStatus(plantao.status));

        if (plantao.place_id && typeof plantao.place_id === "object") {
          setCityId(String((plantao.place_id as any).code ?? (plantao.place_id as any).id ?? ""));
        }
        if (plantao.language_id && typeof plantao.language_id === "object") {
          setLanguageId((plantao.language_id as any).id ?? "");
        }

        const shiftFacilitatorId = String((plantao.facilitator_user_id as any)?.id ?? "");
        setFacilitatorUserId(shiftFacilitatorId);

        const loadedEntries = (plantao.shift_team_shift ?? []).map((entry) => ({
            localId: entry.id,
            id: entry.id,
          interviewerUserId: String((entry.interviewer_user_id as any)?.id ?? ""),
          assistantUserId: String((entry.assistant_user_id as any)?.id ?? ""),
            startDate: formatInTimeZone(entry.start_date_utc, timezone, "yyyy-MM-dd"),
            startTime: formatInTimeZone(entry.start_date_utc, timezone, "HH:mm"),
            endDate: formatInTimeZone(entry.end_date_utc, timezone, "yyyy-MM-dd"),
            endTime: formatInTimeZone(entry.end_date_utc, timezone, "HH:mm")
          }));

        setTeamEntries(loadedEntries.length > 0 ? loadedEntries : [createDefaultTeamEntry(today)]);
        setDeletedEntryIds([]);
      })
      .catch((err) => {
        console.error("Erro ao carregar plantão:", err);
        setError("Erro ao carregar plantão.");
      })
      .finally(() => setLoading(false));
  }, [id, today, timezone]);

  const addTeamEntry = useCallback(() => {
    const referenceDate = preselectedDate || today;

    setTeamEntries((prev) => [
      ...prev,
      createDefaultTeamEntry(referenceDate)
    ]);
  }, [preselectedDate, today]);

  const updateTeamEntry = useCallback(
    (localId: string, field: keyof Omit<TeamEntryForm, "localId" | "id">, value: string) => {
      setTeamEntries((prev) =>
        prev.map((e) => (e.localId === localId ? { ...e, [field]: value } : e))
      );
    },
    []
  );

  const duplicateTeamEntry = useCallback((localId: string) => {
    setTeamEntries((prev) => {
      const entry = prev.find((e) => e.localId === localId);
      if (!entry) return prev;
      const duplicate: TeamEntryForm = {
        localId: `new-${Date.now()}`,
        interviewerUserId: "",
        assistantUserId: "",
        startDate: entry.startDate,
        startTime: entry.startTime,
        endDate: entry.endDate,
        endTime: entry.endTime
      };
      const index = prev.findIndex((e) => e.localId === localId);
      const next = [...prev];
      next.splice(index + 1, 0, duplicate);
      return next;
    });
  }, []);

  const removeTeamEntry = useCallback((localId: string) => {
    setTeamEntries((prev) => {
      if (prev.length <= 1) {
        return prev;
      }

      const entry = prev.find((e) => e.localId === localId);
      if (entry?.id) {
        setDeletedEntryIds((d) => [...d, entry.id!]);
      }
      return prev.filter((e) => e.localId !== localId);
    });
  }, []);

  const hasScopeSelection = useMemo(() => {
    if (mode === ShiftMode.Presential) {
      return Boolean(cityId);
    }

    return Boolean(languageId);
  }, [mode, cityId, languageId]);

  const hasMinimumInterviews = teamEntries.length >= 1;
  const areInterviewsValid = useMemo(
    () => teamEntries.every((entry) => isTeamEntryValid(entry, timezone)),
    [teamEntries, timezone]
  );
  const canSubmit = hasScopeSelection && hasMinimumInterviews && areInterviewsValid;
  const cityNeedsSelection =
    mode === ShiftMode.Presential && citySearch.trim().length > 0 && !cityId;
  const submitDisabledReason = useMemo(() => {
    if (mode === ShiftMode.Presential && !cityId) {
      return cityNeedsSelection
        ? "Selecione um local da lista para continuar."
        : "Selecione um local para continuar.";
    }

    if (mode === ShiftMode.Online && !languageId) {
      return "Selecione um idioma para continuar.";
    }

    if (!hasMinimumInterviews) {
      return "Adicione ao menos uma entrevista.";
    }

    if (!areInterviewsValid) {
      return "Verifique as datas e horários das entrevistas (início deve ser menor que término).";
    }

    return null;
  }, [
    mode,
    cityId,
    cityNeedsSelection,
    languageId,
    hasMinimumInterviews,
    areInterviewsValid
  ]);
  const filteredCities = useMemo(() => {
    const term = citySearch.trim().toLowerCase();
    const visibleCities = !term
      ? cities
      : cities.filter((city) => getCityName(city).toLowerCase().includes(term));

    return [...visibleCities].sort((left, right) =>
      getCityName(left).localeCompare(getCityName(right), "pt-BR", { sensitivity: "base" })
    );
  }, [cities, citySearch]);

  const filteredFacilitatorUsers = useMemo(() => {
    const term = facilitatorSearch.trim().toLowerCase();
    if (!term) return facilitatorUsers;
    return facilitatorUsers.filter((u) => getUserLabel(u).toLowerCase().includes(term));
  }, [facilitatorUsers, facilitatorSearch]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!canSubmit) {
        setError("Preencha os dados obrigatórios e ao menos uma entrevista válida.");
        return;
      }

      setSaving(true);
      setError(null);
      try {
        const interviewsWithDate = teamEntries.map((entry) => ({
          ...entry,
          startIso: toUtcIso(entry.startDate, entry.startTime, timezone),
          endIso: toUtcIso(entry.endDate, entry.endTime, timezone)
        }));

        const shiftStartIso = interviewsWithDate.reduce((min, entry) =>
          new Date(entry.startIso).getTime() < new Date(min).getTime() ? entry.startIso : min,
        interviewsWithDate[0].startIso);

        const shiftEndIso = interviewsWithDate.reduce((max, entry) =>
          new Date(entry.endIso).getTime() > new Date(max).getTime() ? entry.endIso : max,
        interviewsWithDate[0].endIso);

        const normalizedCityCode = cityId.trim();
        const selectedCity = cities.find((city) => getPlaceCode(city) === normalizedCityCode);
        if (mode === ShiftMode.Presential && !selectedCity) {
          setError("Selecione um local valido da lista para continuar.");
          return;
        }

        const selectedCityId = selectedCity ? String(selectedCity.id) : "";
        const selectedCityCode = selectedCity ? getPlaceCode(selectedCity) : "";
        const selectedLanguage = languages.find(
          (language) => String(language.id) === languageId || String(language.code) === languageId
        );
        const selectedLanguageId = selectedLanguage ? String(selectedLanguage.id) : languageId;

        const shiftPayload = {
          mode,
          status,
          start_date_utc: shiftStartIso,
          end_date_utc: shiftEndIso,
          // place_id: mode === ShiftMode.Presential ? selectedCityId || null : null,
          place_id: selectedCityId || null,
          language_id: selectedLanguageId ? selectedLanguageId : null,
          facilitator_user_id: facilitatorUserId || null
        };
        alert(JSON.stringify(shiftPayload, null, 2));
        let shiftId = id;
        if (isEditing && shiftId) {
          await updateShift(shiftId, shiftPayload);
        } else {
          const created = (await createShift(shiftPayload)) as any;
          shiftId = created.id;
        }

        if (!shiftId) throw new Error("Shift ID not available after save.");

        await Promise.all(deletedEntryIds.map((eid) => deleteShiftTeamEntry(eid)));

        await Promise.all(
          interviewsWithDate.map((entry) => {
            const teamPayload = {
              interviewer_user_id: entry.interviewerUserId || null,
              assistant_user_id: entry.assistantUserId || null,
              start_date_utc: entry.startIso,
              end_date_utc: entry.endIso
            };
            if (entry.id) {
              return updateShiftTeamEntry(entry.id, teamPayload);
            }
            return createShiftTeamEntry({ ...teamPayload, shift_id: shiftId! });
          })
        );

        const params = new URLSearchParams();
        params.set("tipo", shiftModeToTipoParam(mode));

        if (mode === ShiftMode.Presential && selectedCityCode) {
          params.set("localId", selectedCityCode);
        }

        navigate(`/plantoes-cadastrados?${params.toString()}`);
      } catch (err) {
        console.error("Erro ao salvar plantão:", err);
        const errorMessage = "Erro ao salvar plantão. Tente novamente.";
        setError(errorMessage);
        window.alert(errorMessage);
      } finally {
        setSaving(false);
      }
    },
    [
      canSubmit,
      mode,
      status,
      cityId,
      cities,
      languageId,
      languages,
      facilitatorUserId,
      id,
      isEditing,
      teamEntries,
      timezone,
      deletedEntryIds,
      navigate
    ]
  );

  return {
    isEditing,
    loading,
    saving,
    error,
    mode,
    setMode,
    status,
    setStatus,
    cityId,
    setCityId,
    citySearch,
    setCitySearch,
    filteredCities,
    cityNeedsSelection,
    languageId,
    setLanguageId,
    facilitatorUserId,
    setFacilitatorUserId,
    facilitatorSearch,
    setFacilitatorSearch,
    filteredFacilitatorUsers,
    cities,
    languages,
    facilitatorUsers,
    interviewerUsers,
    assistantUsers,
    teamEntries,
    addTeamEntry,
    updateTeamEntry,
    duplicateTeamEntry,
    removeTeamEntry,
    canSubmit,
    submitDisabledReason,
    handleSubmit
  };
}
